import React from 'react';
import Icon from '../../../components/AppIcon';

const FX_PRESETS = [
  {
    id: 'clean',
    label: 'Clean',
    settings: { enabled: true, highpassHz: 80, compression: 25, presence: 35, reverb: 8, delay: 0 },
  },
  {
    id: 'rap',
    label: 'Rap Lead',
    settings: { enabled: true, highpassHz: 110, compression: 58, presence: 62, reverb: 12, delay: 8 },
  },
  {
    id: 'rnb',
    label: 'R&B Space',
    settings: { enabled: true, highpassHz: 95, compression: 38, presence: 44, reverb: 34, delay: 18 },
  },
  {
    id: 'wide',
    label: 'Wide Hook',
    settings: { enabled: true, highpassHz: 120, compression: 48, presence: 70, reverb: 28, delay: 32 },
  },
];

const CONTROL_ROWS = [
  { key: 'highpassHz', label: 'High-pass', min: 40, max: 220, step: 5, unit: 'Hz', icon: 'Waves' },
  { key: 'compression', label: 'Compression', min: 0, max: 100, step: 1, unit: '%', icon: 'Gauge' },
  { key: 'presence', label: 'Presence', min: 0, max: 100, step: 1, unit: '%', icon: 'Sparkles' },
  { key: 'reverb', label: 'Reverb', min: 0, max: 100, step: 1, unit: '%', icon: 'Cloud' },
  { key: 'delay', label: 'Delay', min: 0, max: 100, step: 1, unit: '%', icon: 'Repeat' },
];

const VocalFxChain = ({ settings, onChange, disabled = false }) => {
  const current = {
    enabled: false,
    highpassHz: 80,
    compression: 25,
    presence: 35,
    reverb: 10,
    delay: 0,
    ...(settings || {}),
  };

  const update = (patch) => onChange?.({ ...current, ...patch });

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-violet-500/35 bg-violet-950/25 text-violet-200">
            <Icon name="SlidersHorizontal" size={16} />
          </div>
          <div className="min-w-0">
            <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-200">Vocal FX Chain</h3>
            <p className="text-[10px] text-gray-500">Printed into offline bounce and cloud export metadata</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => update({ enabled: !current.enabled })}
          disabled={disabled}
          aria-label={current.enabled ? 'Disable vocal FX chain' : 'Enable vocal FX chain'}
          className={`relative h-7 w-13 rounded-full transition-colors disabled:opacity-40 ${current.enabled ? 'bg-violet-500' : 'bg-gray-700'}`}
        >
          <span className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white transition-transform ${current.enabled ? 'translate-x-6' : 'translate-x-0'}`} />
        </button>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-1">
        {FX_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => update(preset.settings)}
            disabled={disabled}
            className="rounded border border-gray-700 bg-gray-950/35 px-2 py-1.5 text-[10px] font-mono text-gray-300 hover:border-violet-500/50 hover:text-violet-200 disabled:opacity-40"
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className={`space-y-3 ${current.enabled ? '' : 'opacity-45'}`}>
        {CONTROL_ROWS.map((row) => (
          <div key={row.key}>
            <div className="mb-1 flex items-center justify-between gap-2">
              <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-gray-400">
                <Icon name={row.icon} size={11} />
                {row.label}
              </label>
              <span className="text-[10px] font-mono text-violet-200">
                {current[row.key]}{row.unit}
              </span>
            </div>
            <input
              type="range"
              min={row.min}
              max={row.max}
              step={row.step}
              value={current[row.key]}
              disabled={disabled || !current.enabled}
              onChange={(event) => update({ [row.key]: Number(event.target.value) })}
              aria-label={row.label}
              className="h-2 w-full cursor-pointer appearance-none rounded-full disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${((current[row.key] - row.min) / (row.max - row.min)) * 100}%, #374151 ${((current[row.key] - row.min) / (row.max - row.min)) * 100}%, #374151 100%)`,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default VocalFxChain;
